import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, ShieldCheck, MapPin } from "lucide-react";
import { api, fileUrl } from "../lib/api";

/**
 * Horizontal-scroll carousel for public "Explore Property Services" section.
 * Fetches /api/services/professionals which excludes broker + builder.
 */
export default function ServiceProfessionalsCarousel({ compact = false }) {
    const [items, setItems] = useState(null);

    useEffect(() => {
        api.get("/services/professionals", { params: { limit: 12 } })
            .then((r) => setItems(r.data?.items || []))
            .catch(() => setItems([]));
    }, []);

    // Hide the whole section if nothing to show
    if (items === null) return null;
    if (items.length === 0) return null;

    return (
        <section
            className={`${compact ? "py-6" : "py-10 sm:py-16"} bg-white border-t border-navy-500/10`}
            data-testid="home-service-professionals"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6">
                <div className="flex items-end justify-between gap-4 mb-4">
                    <div>
                        <div className="text-[10px] uppercase tracking-widest text-gold-700 font-extrabold flex items-center gap-1.5">
                            <Sparkles className="w-3.5 h-3.5" /> Trusted professionals
                        </div>
                        <h2 className="font-heading font-bold text-2xl sm:text-3xl text-navy-500 mt-1">Explore Property Services</h2>
                        <p className="text-sm text-navy-500/60 mt-1">Find trusted professionals for all your property needs</p>
                    </div>
                    <Link to="/services" className="hidden sm:inline-flex items-center gap-1 text-sm font-bold text-gold-700 hover:underline shrink-0">
                        View all <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>

                {/* Horizontal scroller */}
                <div className="-mx-4 sm:mx-0 overflow-x-auto scrollbar-none">
                    <ul className="flex gap-3 sm:gap-4 px-4 sm:px-0 pb-2 snap-x snap-mandatory">
                        {items.map((p) => (
                            <li key={p.id} className="w-[75vw] sm:w-64 shrink-0 snap-start">
                                <Link to={`/services/professional/${p.id}`} data-testid={`sp-card-${p.id}`} className="block rounded-xl overflow-hidden bg-white border border-navy-500/10 hover:border-gold-500/50 transition-colors shadow-sm">
                                    <div className="aspect-[16/10] bg-navy-500/5 relative">
                                        {p.portfolio_preview?.[0] ? (
                                            <img src={fileUrl(p.portfolio_preview[0].image_url)} alt={p.name} className="w-full h-full object-cover" loading="lazy" />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-navy-900 to-navy-800">
                                                <img src="/gpm-logo.png" alt="GPM" className="w-20 h-20 opacity-80" />
                                            </div>
                                        )}
                                        {p.verified && (
                                            <span className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500 text-white text-[9px] font-bold uppercase tracking-widest">
                                                <ShieldCheck className="w-3 h-3" /> Verified
                                            </span>
                                        )}
                                    </div>
                                    <div className="p-3">
                                        <div className="text-[10px] uppercase tracking-widest text-gold-700 font-bold">{p.category_label}</div>
                                        <div className="font-heading font-bold text-navy-500 text-sm mt-0.5 truncate">{p.name}</div>
                                        {p.city && (
                                            <div className="text-[11px] text-navy-500/55 mt-0.5 flex items-center gap-1">
                                                <MapPin className="w-3 h-3" /> {p.city}
                                            </div>
                                        )}
                                        <div className="mt-2 inline-flex items-center gap-1 text-[11px] font-extrabold text-navy-500 px-2.5 py-1 rounded bg-gold-500 hover:bg-gold-400">
                                            View Services <ArrowRight className="w-3 h-3" />
                                        </div>
                                    </div>
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="sm:hidden mt-4 text-center">
                    <Link to="/services" className="inline-flex items-center gap-1 text-sm font-bold text-gold-700">
                        View all professionals <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            </div>
        </section>
    );
}
